import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { mapAuthError } from '@/lib/auth-errors';
import { useNavLayer } from '@/hooks/useNavLayer';
import { useDefinitelyOffline } from '@/lib/reading-access';
import { countBsbBooks } from '@/lib/bsb-db';
import { BIBLE_ENABLED } from '@/lib/feature-flags';
import HomePrayerScene from './HomePrayerScene';
import AuthModal from './AuthModal';
import LanguageSwitcher from './LanguageSwitcher';
import styles from './AuthGateScreen.module.css';

interface AuthGateScreenProps {
  // 'loading' shows the calm scene + a faint breathing dot (session resolving);
  // 'signin' shows the brand wordmark + reverent subhead + sign-in controls.
  // Same component for both phases so hooks stay stable across the transition.
  phase: 'loading' | 'signin';
}

/** Existing neutral loading presentation without starting another auth hook. */
export function AuthGateLoadingScene() {
  const { t } = useTranslation();
  return <div className={styles.root}>
    <HomePrayerScene active={false} saved={false} hasText={false} />
    <div className={styles.scrim} />
    <div className={styles.cluster} role="status" aria-label={t('auth.gateLoadingAria')}>
      <div className={styles.loadingDot} />
    </div>
  </div>;
}

/**
 * The beautiful sign-in gate surface. The signature tree-by-stream
 * HomePrayerScene fills the screen; a scrim + centered cluster carry the brand
 * wordmark and sign-in affordances. Rendered by AuthGate when there is no
 * session. See docs/plans/auth-gate/auth-gate-2026-06-25.md.
 */
export default function AuthGateScreen({ phase }: AuthGateScreenProps) {
  const { t } = useTranslation();
  const { signInWithGoogle } = useAuth();
  const [showEmail, setShowEmail] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  // An i18n KEY, rendered with t() below, so a message already on screen
  // follows a language change instead of staying in the old language.
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const offline = useDefinitelyOffline();
  const [cachedBible, setCachedBible] = useState(false);
  useEffect(() => {
    if (!offline || phase !== 'signin' || !BIBLE_ENABLED) return;
    let disposed = false;
    // Public cache only, through the existing readonly/versionless path.
    // Neither a failed read nor an uncached install creates a Bible affordance.
    void countBsbBooks().then((count) => {
      if (!disposed) setCachedBible(count > 0);
    }).catch(() => { if (!disposed) setCachedBible(false); });
    return () => { disposed = true; };
  }, [offline, phase]);

  // NavStack layer for the email modal (rule #78), mirroring AuthSection's
  // `settings-auth`. Hook is unconditional; the screen mounts with
  // showEmail=false so the layer registers closed (StrictMode-safe).
  const zAuth = useNavLayer('gate-auth', showEmail, () => setShowEmail(false));

  // Host-owned Escape: AuthModal has no internal Escape handler (mirrors
  // AuthSection). setShowEmail(false) routes through the useNavLayer teardown
  // to requestClose, converging with X / backdrop / browser-back.
  useEffect(() => {
    if (!showEmail) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowEmail(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showEmail]);

  async function handleGoogle() {
    setErrorKey(null);
    setSigningIn(true);
    const result = await signInWithGoogle();
    if (result.error) {
      // Provider misconfig / OAuth error: surface it (the direct CTA bypasses
      // AuthModal's handleGoogle catch, so it would otherwise fail silently).
      // Localized via the shared mapper so no raw English Supabase string leaks
      // (rule #6), same as AuthModal.
      setErrorKey(mapAuthError(result.error));
      setSigningIn(false);
    }
    // Success is a full-page redirect; leave signingIn true as the page leaves.
  }

  return (
    <div className={styles.root}>
      <HomePrayerScene active={false} saved={false} hasText={false} />
      <div className={styles.scrim} />

      {phase === 'loading' ? (
        <div
          className={styles.cluster}
          role="status"
          aria-label={t('auth.gateLoadingAria')}
        >
          <div className={styles.loadingDot} />
        </div>
      ) : (
        <div className={styles.cluster}>
          {/* Brand wordmark. Literal (rule #6 brand exception; CLAUDE.md fixes
              "watch Him work" casing + the gold "Him"). Static gold, NOT the
              breathing filter-glow, so no GPU layer composites under the email
              AuthModal (iOS modal-stacking note). */}
          <h1 className={styles.brand}>
            watch <span className={styles.gold}>Him</span> work
          </h1>
          <p className={styles.subhead}>{t('auth.gateSubhead')}</p>

          <div className={styles.controls}>
            <button
              type="button"
              className={styles.googleBtn}
              onClick={handleGoogle}
              disabled={signingIn}
            >
              <svg
                className={styles.socialIcon}
                viewBox="0 0 24 24"
                width="20"
                height="20"
                aria-hidden="true"
              >
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              {signingIn ? t('auth.gateOpening') : t('auth.continueGoogle')}
            </button>

            <button
              type="button"
              className={styles.emailBtn}
              onClick={() => setShowEmail(true)}
            >
              {t('auth.continueEmail')}
            </button>

            {errorKey && <p className={styles.error}>{t(errorKey)}</p>}
            {offline && <p role="status" className={styles.offlineLine}>{t('offlineReopen.readingOnly')}</p>}
            {offline && BIBLE_ENABLED && cachedBible && <Link className={styles.bibleBtn} to="/bible">
              {t('offlineReopen.readBible')}
            </Link>}
          </div>

          {/* Language before sign-in (P-LANG): the same control as Settings,
              using the saved `adia-lang` preference. No onboarding step. */}
          <div className={styles.languages}>
            <LanguageSwitcher />
          </div>
        </div>
      )}

      {showEmail && (
        <AuthModal onClose={() => setShowEmail(false)} zIndex={zAuth} />
      )}
    </div>
  );
}
